import { ExternalSystem, toExternalSystem } from '@monetize/types/app';

export interface ExternalRecordLinkConfig {
  salesforceInstanceUrl?: string | null;
  hubspotPortalId?: string | null;
}

/**
 * HubSpot's record URL needs the target object's type id, which the sync log
 * doesn't carry directly — only the MonetizeNow `entityName` that was synced.
 * Keyed on the raw connector value rather than `EntityNameEnum`: the connector
 * also emits `entityName: "Contact"` for contact syncs (see
 * ASSOCIATION_ENTITY_TO_MN_ENTITY_NAME in associations.types.ts), a value
 * `EntityNameEnum` doesn't declare. Only the objects MonetizeNow links to
 * HubSpot today are mapped; anything else renders as plain text.
 */
const HUBSPOT_OBJECT_TYPE_ID_BY_ENTITY_NAME: Record<string, string> = {
  Account: '0-2', // Company
  Contact: '0-1',
  Opportunity: '0-3', // Deal
  Quote: '0-3', // Deal
};

/**
 * `salesforceInstanceUrl` is stored connector configuration rather than user
 * input, but it is interpolated straight into an `href` — any scheme other
 * than http(s) there (`javascript:`, `data:`) would render as a clickable
 * script URL. Returns the origin, which also drops the trailing slash the
 * connector sometimes stores.
 */
function toHttpOrigin(instanceUrl: string): string | undefined {
  try {
    const { protocol, origin } = new URL(instanceUrl);
    return protocol === 'http:' || protocol === 'https:' ? origin : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Builds a deep link to a synced record in its external system, when the
 * frontend has enough information to construct one.
 *
 * - Salesforce: `{instanceUrl}/{targetId}` resolves for any object type.
 * - HubSpot: `https://app.hubspot.com/contacts/{portalId}/record/{objectTypeId}/{targetId}`,
 *   for the Account/Contact/Opportunity/Quote entities above.
 * - Other systems' URL schemes (Attio, Rillet, QuickBooks, NetSuite, Xero)
 *   aren't known yet, so they render as plain text.
 */
export function getExternalRecordUrl(
  targetSystemType: string,
  entityName: string,
  targetId: string | null | undefined,
  { salesforceInstanceUrl, hubspotPortalId }: ExternalRecordLinkConfig,
): string | undefined {
  if (!targetId) {
    return undefined;
  }

  const system = toExternalSystem(targetSystemType);

  const encodedTargetId = encodeURIComponent(targetId);

  if (system === ExternalSystem.SALESFORCE && salesforceInstanceUrl) {
    const origin = toHttpOrigin(salesforceInstanceUrl);
    if (origin) {
      return `${origin}/${encodedTargetId}`;
    }
  }

  if (system === ExternalSystem.HUBSPOT && hubspotPortalId) {
    const objectTypeId = HUBSPOT_OBJECT_TYPE_ID_BY_ENTITY_NAME[entityName];
    if (objectTypeId) {
      return `https://app.hubspot.com/contacts/${encodeURIComponent(hubspotPortalId)}/record/${objectTypeId}/${encodedTargetId}`;
    }
  }

  return undefined;
}
